import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, KeyRound, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teams");
      setTeams(data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/teams", { name: newName.trim() });
      setNewName("");
      toast.success("Team created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create team");
    } finally {
      setBusy(false);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setBusy(true);
    try {
      await api.post("/teams/join", { invite_code: inviteCode.trim().toUpperCase() });
      setInviteCode("");
      toast.success("Joined team");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid invite code");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Invite code ${code} copied`);
  };

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="section-label mb-3">teams · roster</div>
        <h1 className="font-display font-bold text-4xl tracking-tight">Your Teams</h1>
        <p className="mt-2 font-mono text-sm text-muted-foreground">{">"} create a squad or join with an invite code</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Create */}
          <form onSubmit={create} className="border border-border bg-card p-6 space-y-4" data-testid="create-team-form">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <Plus className="h-3.5 w-3.5 text-primary" /> create_team
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-name" className="font-mono text-[11px] uppercase tracking-widest">team_name</Label>
              <Input
                id="team-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="GTGS Devs"
                className="rounded-none font-mono"
                maxLength={80}
                data-testid="create-team-name-input"
              />
            </div>
            <Button type="submit" disabled={busy || !newName.trim()} className="rounded-none font-mono uppercase tracking-widest" data-testid="create-team-submit">
              {busy ? "Working…" : "Create Team"}
            </Button>
          </form>

          {/* Join */}
          <form onSubmit={join} className="border border-border bg-card p-6 space-y-4" data-testid="join-team-form">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <KeyRound className="h-3.5 w-3.5 text-primary" /> join_with_code
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-code" className="font-mono text-[11px] uppercase tracking-widest">invite_code</Label>
              <Input
                id="invite-code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="ABC23XYZ"
                className="rounded-none font-mono uppercase tracking-widest"
                maxLength={20}
                data-testid="join-team-code-input"
              />
            </div>
            <Button type="submit" disabled={busy || !inviteCode.trim()} variant="outline" className="rounded-none border-border font-mono uppercase tracking-widest" data-testid="join-team-submit">
              {busy ? "Working…" : "Join Team"}
            </Button>
          </form>
        </div>

        <div className="mt-12">
          <div className="section-label mb-3">my_teams</div>
          {loading ? (
            <div className="font-mono text-sm text-muted-foreground">loading…</div>
          ) : teams.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center font-mono text-sm text-muted-foreground" data-testid="teams-empty">
              {">"} no teams yet. create one above or join with a code.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="teams-list">
              {teams.map((t) => (
                <div key={t.id} className="group bg-card p-5 flex flex-col transition-colors hover:bg-primary/5" data-testid={`team-card-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">// team</div>
                      <div
                        className="font-display font-bold text-xl mt-1 truncate glitch"
                        data-text={t.name}
                        title={t.name}
                      >
                        {t.name}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 font-mono text-xs text-muted-foreground shrink-0">
                      <Users className="h-3.5 w-3.5" />
                      <span className="num-mono">{t.member_count}</span>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 border border-border px-2 py-1 flex-1 min-w-0">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">code</span>
                      <span className="num-mono text-sm text-primary truncate" data-testid={`team-invite-${t.id}`}>{t.invite_code}</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="rounded-none border-border"
                      onClick={() => copyCode(t.invite_code)}
                      data-testid={`team-copy-${t.id}`}
                      aria-label="Copy invite code"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="mt-4 flex">
                    <Link to={`/teams/${t.id}`} className="ml-auto">
                      <Button variant="ghost" size="sm" className="rounded-none font-mono text-xs uppercase tracking-widest" data-testid={`team-open-${t.id}`}>
                        Open <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
